import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMutation } from "@apollo/react-hooks";
import { Box } from "@mui/material";
import { MyTimesheetRoleFilter } from "my-timesheet/components";
import {
  myTimesheetEmplyeeRequests,
  myTimesheetEmplyeeRequestsIsMobile,
} from "my-timesheet/configs/table-defs";
import { MY_TIME_SHEET_GET_USERS_SUMMARY_LIST } from "my-timesheet/graphql";
import { OcCard, OcDataTable } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcCurrentUserState,
  useOcLocalState,
  useOcRoleFilters,
} from "oc/hooks";

type MyTimesheetUserRequestsRequestsProps = {
  periodStart: string;
  periodEnd: string;
  product?: string;
  role: string;
  isMobile: boolean;
};

export const MyTimesheetUserRequestsRequests = ({
  periodStart,
  periodEnd,
  product = "MyTimesheet",
  role,
  isMobile,
}: MyTimesheetUserRequestsRequestsProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [onlyRequested, setOnlyRequested] = useOcLocalState(
    "showOnlyRequested",
    true,
  );
  const [selectedRowId, setSelectedRowId] = useState<string | undefined>(
    undefined,
  );

  let { contractCode: contract } = useOcCurrentUserState();

  const {
    roleFilters,
    prevRoleFilters,
    resetRoleFilters,
    setPrevRoleFilters,
    handleAddFilter,
    handleRemoveFilter,
  } = useOcRoleFilters({
    contract,
    product,
    role,
  });

  let tableId = "myTimesheetEmployeeRequestsData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "employeeName",
    pageSize: 5,
  });

  type UsersSummaryListRow = {
    contractId: string;
    _id: string;
    employeeName: string;
    requestedCost: number;
    requestedDuration: string;
    requests: number;
    projectName?: string;
    clientName?: string;
    startTime?: string;
    endTime?: string;
    duration?: string;
  };

  type UsersSummaryListData = {
    usersSummaryList?: {
      rows?: UsersSummaryListRow[];
      // add other fields as needed
    };
  };

  const [data, setData] = useState<UsersSummaryListData | undefined>();

  const [runQuery, { loading, error }] = useMutation(
    MY_TIME_SHEET_GET_USERS_SUMMARY_LIST,
    {
      variables: {
        page: 1,
        pageSize: 10,
        startTime: periodStart,
        endTime: periodEnd,
        roleFilters,
        role,
        sortField: tableData?.sortField,
        sortDirection: tableData?.sortDirection,
        searchField: tableData?.searchField,
        searchText: tableData?.searchText,
        onlyRequested,
        skipQuery: true,
      },
      onCompleted(data) {
        setData(data);
      },
    },
  );

  const list = data?.usersSummaryList?.rows || [];

  const handleSelectRequest = (row: any) => {
    const selected = isMobile ? row.row : row;
    setSelectedRowId(selected.contractId);
    history.push(`/my-timesheet/user-requests/${selected.contractId}`);
  };

  const onSelectRow = (row: UsersSummaryListRow) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === row?._id ? undefined : row?._id);
    } else {
      handleSelectRequest(row);
    }
  };

  const formattedData = list?.map((row) => {
    const workDate = row.startTime
      ? new Date(row.startTime).toLocaleDateString("hu-HU", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : "";
    const startTime = row.startTime
      ? new Date(row.startTime).toLocaleTimeString("hu-HU", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";
    const endTime = row.endTime
      ? new Date(row.endTime).toLocaleTimeString("hu-HU", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

    return {
      ...row,
      employeeName: t(row.employeeName),
      requestedCost: new Intl.NumberFormat("hu-HU", {
        style: "currency",
        currency: "HUF",
        maximumSignificantDigits: 3,
      }).format(row.requestedCost),
      workDate,
      startTime,
      endTime,
    };
  });

  const requestsData = {
    rows: formattedData,
    total: formattedData?.length,
    page: tableData?.page,
    pageSize: tableData?.pageSize,
  };

  const handleTableDataChange = (state: typeof initialTableData) => {
    const keys = Object.keys(state).toString();
    setTableData(state);
    if (
      keys === "searchField" ||
      (keys === "searchText" && state?.searchText?.length < 3)
    ) {
      return;
    }
    runQuery();
  };

  useEffect(() => {
    if (tableData?.searchText?.length >= 3 || !tableData?.searchText) {
      runQuery();
    }
  }, [periodStart, periodEnd, tableData?.searchText]); //eslint-disable-line

  const tableDef = isMobile
    ? myTimesheetEmplyeeRequestsIsMobile
    : myTimesheetEmplyeeRequests;

  const filteredTableDef = {
    id: myTimesheetEmplyeeRequests?.id,
    columns: myTimesheetEmplyeeRequests?.columns?.filter(
      (column) =>
        !myTimesheetEmplyeeRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  return (
    <OcCard
      label={t("MY_TIMESHEET_REQUESTS")}
      loading={loading}
      error={!!error}
    >
      <Box height="16px" />
      <MyTimesheetRoleFilter
        data={data ?? {}}
        product={product}
        role={role}
        refetch={runQuery}
        contract={contract}
        resetRoleFilters={resetRoleFilters}
        roleFilters={roleFilters}
        prevRoleFilters={prevRoleFilters}
        setPrevRoleFilters={setPrevRoleFilters}
        handleAddFilter={handleAddFilter}
        handleRemoveFilter={handleRemoveFilter}
        buttonGroup={true}
        runQuery={runQuery}
        setOnlyRequested={setOnlyRequested}
      />
      <Box height="32px" />
      <OcDataTable
        data={requestsData}
        loading={loading}
        tableDef={tableDef}
        tableData={tableData}
        pagination={true}
        onSelectRow={onSelectRow}
        localPaging={true}
        selectedRowId={selectedRowId}
        setTableData={handleTableDataChange}
        filteredTableDef={filteredTableDef}
        handleMoreButton={handleSelectRequest}
      />
    </OcCard>
  );
};

export default MyTimesheetUserRequestsRequests;
